/**
 * Sparks coach marks: two short nav tips, "spend in the shop" and "earn by answering", shown once
 * per child login and again after every NAV_CLICKS_PER_TIP nav clicks, alternating so a child sees
 * both. Each is followed by one section's "how to earn" tip (SECTION_TIPS), rotating. State lives in localStorage per child (siblings share a device). Losing it only costs one
 * extra tip, so it needs no API. MAX_SHOWINGS is a very high cap for now; lower it to retire the
 * tips once a child has seen them enough.
 */
export const TIPS = {
  shop: { target: 'shop', text: "Spend your Sparks in Addy's Shop to dress Addy up!" },
  sparks: { target: 'sparks', text: 'Answer questions to earn Sparks ✨' },
  // Not in the login/nav rotation: shown by SparksCoachMark when the shop alert asks (shopNudge.js).
  canBuy: { target: 'shop', text: "You've got enough Sparks for something new! ✨" },
};
/**
 * "How to earn here" tips under a section's nav tab, one per login or nav trigger, following the shop or Sparks tip
 * and rotating in this order. `rates` is the state's `earnRates`, so the numbers follow the live config.
 */
export const SECTION_TIPS = {
  coreSkills: {
    target: 'core-skills',
    onPage: (p) => p === '/core-skills' || p.startsWith('/core-skills/'),
    text: (r) => `Core Skills earn the most Sparks! ${r.coreSkillCorrect} for every right answer and up to ${r.coreSkillQuiz} for finishing ✨`,
  },
  worksheets: {
    target: 'worksheets',
    onPage: (p) => p.startsWith('/worksheets'),
    text: (r) => `Finish a worksheet of ${r.worksheetMinQuestions} or more questions for ${r.worksheet} Sparks, plus ${r.worksheetCorrect} for each right answer. The same lessons earn once a day ✨`,
  },
  homeLearning: {
    target: 'lets-learn',
    onPage: (p) => p === '/' || p === '/lets-learn',
    text: (r) => `Finish a lesson for ${r.lessonFinished} Sparks, then its quiz for ${r.quiz} more, plus ${r.standardCorrect} for every right answer${r.homeLearningOncePerDay ? '. Each lesson earns once a day' : ''}${r.homeLearningRates ? '. Tricky topics pay bonus Sparks ⚡' : ' ✨'}`,
  },
  assignments: {
    target: 'homework',
    onPage: (p) => p.startsWith('/homework'),
    text: (r) => `Finish an assignment for ${r.homework} Sparks, plus ${r.standardCorrect} for every right answer ✨`,
  },
};
const SECTION_ORDER = Object.keys(SECTION_TIPS);
export const SECTION_TIP_DELAY_MS = 600;
export const NAV_CLICKS_PER_TIP = 10;
export const MAX_SHOWINGS = 1000;
export const TIP_AUTO_DISMISS_MS = 8000;

const EMPTY = { navClicks: 0, lastTip: null, showings: 0, pending: null, lastSection: null };
const key = (childId) => `sparksTips:${childId}`;

const storageOf = (storage) => storage || (typeof localStorage !== 'undefined' ? localStorage : null);

function read(childId, storage) {
  try {
    const raw = storageOf(storage)?.getItem(key(childId));
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : { ...EMPTY };
  } catch (e) {
    return { ...EMPTY };
  }
}

function write(childId, state, storage) {
  try {
    storageOf(storage)?.setItem(key(childId), JSON.stringify(state));
  } catch (e) {
    // Private mode / blocked storage: the tip simply does not persist.
  }
}

/** A child has just logged in: queue a tip for the next nav render. */
export function noteChildLogin(childId, storage) {
  if (!childId) return;
  write(childId, { ...read(childId, storage), pending: 'login' }, storage);
}

/** A nav link was clicked: every NAV_CLICKS_PER_TIP clicks queues a tip. */
export function noteNavClick(childId, storage) {
  if (!childId) return;
  const s = read(childId, storage);
  const navClicks = s.navClicks + 1;
  write(childId, { ...s, navClicks, pending: navClicks % NAV_CLICKS_PER_TIP === 0 ? 'nav' : s.pending }, storage);
}

/**
 * Consume the queued tip, if any, and return { tip, trigger } or null. Alternates with the last tip
 * shown; `avoid` names a tip that makes no sense right now (the shop tip on the shop page), in
 * which case the other one is shown instead.
 */
export function takePendingTip(childId, { avoid, storage } = {}) {
  if (!childId) return null;
  const s = read(childId, storage);
  if (!s.pending || s.showings >= MAX_SHOWINGS) return null;
  let tip = s.lastTip === 'shop' ? 'sparks' : 'shop';
  if (tip === avoid) tip = tip === 'shop' ? 'sparks' : 'shop';
  write(childId, { ...s, pending: null, lastTip: tip, showings: s.showings + 1 }, storage);
  return { tip, trigger: s.pending };
}

/** The next section tip in rotation after the last one shown, skipping the section on screen (`pathname`). */
export function takeSectionTip(childId, { pathname = '', storage } = {}) {
  if (!childId) return null;
  const s = read(childId, storage);
  const from = SECTION_ORDER.indexOf(s.lastSection);
  let section = SECTION_ORDER[(from + 1) % SECTION_ORDER.length];
  if (SECTION_TIPS[section].onPage(pathname)) section = SECTION_ORDER[(SECTION_ORDER.indexOf(section) + 1) % SECTION_ORDER.length];
  write(childId, { ...s, lastSection: section }, storage);
  return section;
}
